import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { useBridge } from '@onl-web/shared/bridge'

import FilterChips, { type ChipItem } from '@/components/FilterChips'
import Tabs, { type TabItem } from '@/components/Tabs'
import TopButton from '@/components/TopButton'
import { useHasToken } from '@/hooks/useHasToken'
import { useGetRegionDetail } from '@/query/serviceArea/useGetRegionDetail'
import { useGetRegionList } from '@/query/serviceArea/useGetRegionList'

import ContentSpinner from './components/ContentSpinner'
import HeroSection from './components/HeroSection'
import RegionMap from './components/RegionMap'
import SiGunGuChildren from './components/SiGunGuChildren'
import SiGunGuGroup from './components/SiGunGuGroup'
import { ALL_SUB_REGION, HIDDEN_REGIONS, regionOrderIndex } from './constants/regionTabs'
import { ContentSection, DistrictList, FilterRow, PageWrapper, Sentinel, SiDoTitle, StickyTabs } from './styles'
import type { RegionName } from './types/region'
import { scrollToSiGunGu } from './utils/scrollToSiGunGu'
import { type AccordionKey, buildAccordionKey, getSiGunGuAccordionId } from './utils/sigunguAccordion'

export default function ServiceAreaPage() {
  /**
   * 회원 여부 — tokenStore reactive subscription.
   *
   * useSyncExternalStore 로 token 변경을 즉시 반영 (예: 회원 탈퇴 후 재가입으로 새 token 발급되는 흐름).
   * 페이지가 unmount/remount 되지 않는 케이스(같은 webview 재사용)에서도 검색창 노출 자동 갱신.
   */
  const isAuthenticated = useHasToken()

  const { data: regionList } = useGetRegionList()

  /**
   * 노출 가능한 region 만 추림 + 명시적 순서 정렬.
   * 백엔드 응답 순서가 달라도 클라가 한 번 더 가드 (서울 → 경기 → 인천 → 그 외).
   */
  const visibleRegionList = useMemo(
    () =>
      (regionList ?? [])
        .filter((r) => !HIDDEN_REGIONS.has(r.region))
        .slice()
        .sort((a, b) => regionOrderIndex(a.region) - regionOrderIndex(b.region)),
    [regionList]
  )

  /**
   * 첫 region 자동 선택 — 최초 1회 초기화.
   * 응답 도착 전엔 null, 응답 도착 시 첫 region을 setState. `=== null` 가드가 재진입(무한 렌더)을 막는다.
   * lazy initializer로 처리 못 하는 이유: useState 초기화 시점엔 regionList가 아직 없음.
   */
  const [selectedRegion, setSelectedRegion] = useState<RegionName | null>(null)
  if (selectedRegion === null && visibleRegionList.length > 0) {
    setSelectedRegion(visibleRegionList[0].region)
  }

  /** 하위 광역권역 필터. null이면 전체. region 변경 시 자동 리셋 */
  const [selectedSubRegion, setSelectedSubRegion] = useState<string | null>(null)

  /** 펼쳐진 동 그룹 키 집합. 다중 펼침 허용 — 사용자가 여러 그룹을 동시에 열어서 비교 가능 */
  const [openedKeys, setOpenedKeys] = useState<Set<AccordionKey>>(() => new Set())

  const { callHandler } = useBridge()

  /**
   * Tabs sticky pinned 감지 — sentinel(Tabs 위 0px box) 이 viewport 를 벗어나는 시점이 곧 pinned 시점.
   * Tabs 가 viewport top 에 고정되면 = sentinel 이 그 위로 사라짐 = !isIntersecting = isPinned.
   * isPinned 가 곧 TopButton 노출 조건.
   */
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isPinned, setIsPinned] = useState(false)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(([entry]) => setIsPinned(!entry.isIntersecting), { threshold: 0 })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  /**
   * 광역권역 상세 — 두 가지 용도로 분리 호출.
   *
   * 1. fullDetail (subRegion=null) — 칩 items(`subRegionList`) 기준.
   *    "전체" 응답에는 모든 subRegion 이 포함되므로 사용자가 어느 칩을 눌러도 칩 row 가 안정적.
   * 2. regionDetail (현재 subRegion) — 시군구 카드(`siDoList`) 기준.
   *    필터링된 subRegion 의 시군구만 노출.
   *
   * 같은 query 가 둘 동시 마운트되어도 dedupe + staleTime: Infinity 로 추가 호출 없음.
   */
  const { data: fullDetail } = useGetRegionDetail({ region: selectedRegion, subRegion: null })
  const { data: regionDetail } = useGetRegionDetail({ region: selectedRegion, subRegion: selectedSubRegion })

  const tabItems = useMemo<TabItem<RegionName>[]>(
    () => visibleRegionList.map((r) => ({ value: r.region, label: r.region })),
    [visibleRegionList]
  )

  /**
   * subRegion 칩 items — 전체 응답(fullDetail) 기준이라 어느 칩을 눌러도 row 가 안정.
   * subRegionList 비었으면 빈 배열로 (칩 row 자체 숨김).
   */
  const subRegionChipItems = useMemo<ChipItem<string>[]>(() => {
    if (!fullDetail || fullDetail.subRegionList.length === 0) return []
    return [
      { value: ALL_SUB_REGION, label: '전체' },
      ...fullDetail.subRegionList.map((sr) => ({ value: sr, label: sr })),
    ]
  }, [fullDetail])

  /** 광역권역 레벨이 isFullCoverage=true면 시군구 분기 의미 없음 → 카드 리스트 숨김 */
  const showDistrictList = regionDetail ? !regionDetail.isFullCoverage : false

  /** 시도 헤더 노출 여부 — siDoList가 2개 이상일 때만 (예: 경상 = 부산광역시 + 경상남도) */
  const showSiDoTitle = (regionDetail?.siDoList.length ?? 0) > 1

  /**
   * 인천 분기 — 피그마 17740:44638.
   * SiGunGuGroup 헤더("계양구") 숨김 + DongAccordion 카드 라벨에 시군구 이름 직접 노출 (단일 카드 평면 나열).
   */
  const isIncheon = selectedRegion === '인천'

  /**
   * 경기 분기 — 시군구 isFullCoverage 시 정적 "전 지역 수거 가능" 카드로 교체 (구리·부천·안양·의정부 등).
   * 인천은 chip 패턴 유지하므로 본 분기와 별도. 다른 시도(서울·경상 등)도 isFullCoverage 케이스 발생 시 보수적으로 미적용.
   */
  const isGyeonggi = selectedRegion === '경기'

  const handleRegionChange = (next: RegionName) => {
    setSelectedRegion(next)
    setSelectedSubRegion(null)
    setOpenedKeys(new Set())
  }

  const handleSubRegionChange = (value: string) => {
    setSelectedSubRegion(value === ALL_SUB_REGION ? null : value)
    setOpenedKeys(new Set())
  }

  const toggleAccordion = (key: AccordionKey) => {
    setOpenedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  /**
   * 지도 라벨 클릭 — 해당 시군구 카드 펼침 + 스크롤.
   *
   * 모든 시군구 카드는 펼침 가능(isFullCoverage 포함) — 사용자가 동 정보 확인 가능.
   * 일반구 보유 시군구는 모든 일반구 카드 한 번에 펼침 (시군구 단위 비교 UX).
   *
   * setState 후 scrollIntoView는 다음 프레임에 — 펼침으로 인한 layout shift 후 정확한 위치로 스크롤.
   */
  const handleLabelClick = (sigunguName: string) => {
    const siGunGu = regionDetail?.siDoList.flatMap((siDo) => siDo.siGunGuList).find((s) => s.name === sigunguName)
    if (!siGunGu) return

    const keysToOpen: AccordionKey[] =
      siGunGu.ilbanGuList.length > 0
        ? siGunGu.ilbanGuList.map((ig) => buildAccordionKey(sigunguName, ig.name))
        : [buildAccordionKey(sigunguName, null)]
    setOpenedKeys((prev) => {
      const next = new Set(prev)
      keysToOpen.forEach((k) => next.add(k))
      return next
    })

    requestAnimationFrame(() => scrollToSiGunGu(sigunguName))
  }

  return (
    <PageWrapper>
      <HeroSection
        onSearchClick={() => {
          // 페이지 전환은 네이티브 책임 — 웹은 핸들러 호출만
          void callHandler('onNextRouter', { key: 'search' })
        }}
        showSearch={isAuthenticated}
      />
      <Sentinel
        ref={sentinelRef}
        aria-hidden='true'
      />
      {selectedRegion ? (
        <>
          <StickyTabs>
            <Tabs
              items={tabItems}
              value={selectedRegion}
              onChange={handleRegionChange}
            />
          </StickyTabs>
          <ContentSection>
            {regionDetail ? (
              <>
                {subRegionChipItems.length > 0 && (
                  <FilterRow>
                    <FilterChips
                      items={subRegionChipItems}
                      value={selectedSubRegion ?? ALL_SUB_REGION}
                      onChange={handleSubRegionChange}
                    />
                  </FilterRow>
                )}
                <RegionMap
                  region={selectedRegion}
                  isFullCoverage={regionDetail.isFullCoverage}
                  subRegion={selectedSubRegion}
                  onLabelClick={handleLabelClick}
                />
                {showDistrictList && (
                  <DistrictList compact={isIncheon}>
                    {regionDetail.siDoList.map((siDo) => (
                      <Fragment key={siDo.name}>
                        {showSiDoTitle && <SiDoTitle>{siDo.name}</SiDoTitle>}
                        {siDo.siGunGuList.map((siGunGu) => (
                          <SiGunGuGroup
                            key={siGunGu.name}
                            id={getSiGunGuAccordionId(siGunGu.name)}
                            name={siGunGu.name}
                            showTitle={!isIncheon}>
                            <SiGunGuChildren
                              siGunGu={siGunGu}
                              openedKeys={openedKeys}
                              onToggle={toggleAccordion}
                              partialGroupLabel={isIncheon ? siGunGu.name : undefined}
                              showStaticFullCoverage={isGyeonggi}
                            />
                          </SiGunGuGroup>
                        ))}
                      </Fragment>
                    ))}
                  </DistrictList>
                )}
              </>
            ) : (
              // regionDetail 응답 대기 — 첫 region 자동 선택 직후 또는 새 region 클릭 후 잠깐
              <ContentSpinner />
            )}
          </ContentSection>
        </>
      ) : (
        // 첫 진입 — regionList 응답 대기. Tabs/ContentSection 자체가 안 그려지므로 페이지 영역에 스피너
        <ContentSpinner />
      )}
      <TopButton visible={isPinned} />
    </PageWrapper>
  )
}
